// src/services/MobileDetectionService.js
// Enterprise-grade mobile detection service - Single source of truth
// Fortune 500 quality with comprehensive device detection

export class MobileDetectionService {
    static _instance = null;
    static _cache = new Map();
    static _observers = new Set();
    
    // Singleton pattern for enterprise consistency
    static getInstance() {
        if (!MobileDetectionService._instance) {
            MobileDetectionService._instance = new MobileDetectionService();
        }
        return MobileDetectionService._instance;
    }
    
    constructor() {
        if (MobileDetectionService._instance) {
            return MobileDetectionService._instance;
        }
        
        this._initializeDetection();
        this._setupResizeObserver();
        MobileDetectionService._instance = this;
    }
    
    /**
     * Primary mobile detection method - Enterprise grade
     * @returns {boolean} True if mobile device detected
     */
    static isMobile() {
        const cacheKey = 'isMobile';
        
        if (MobileDetectionService._cache.has(cacheKey)) {
            return MobileDetectionService._cache.get(cacheKey);
        }
        
        const result = this._detectMobile();
        MobileDetectionService._cache.set(cacheKey, result);
        
        return result;
    }
    
    /**
     * Comprehensive mobile detection logic
     * @private
     */
    static _detectMobile() {
        // User Agent detection (primary)
        const userAgentMobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone/i.test(navigator.userAgent);
        
        // Screen size detection (secondary)
        const screenSizeMobile = window.innerWidth < 768;
        
        // Touch capability detection (tertiary)
        const touchCapable = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        
        // Orientation API detection (quaternary)
        const orientationAPI = 'orientation' in window || 'screen' in window && 'orientation' in screen;
        
        // Device pixel ratio consideration (for high-DPI mobile devices)
        const highDPI = window.devicePixelRatio > 1.5;
        
        // ENTERPRISE: More accurate mobile detection (prevent touchscreen desktop false positives)
        let mobileScore = 0;
        
        // User agent is primary and most reliable indicator
        if (userAgentMobile) mobileScore += 6; // Increased weight for user agent
        
        // Screen size is secondary but important
        if (screenSizeMobile) mobileScore += 3; // Increased weight for screen size
        
        // Touch capability is less reliable (many desktops have touch)
        if (touchCapable && screenSizeMobile) mobileScore += 2; // Only count touch if screen is small
        
        // Orientation API is weak indicator
        if (orientationAPI && screenSizeMobile) mobileScore += 1; // Only count if screen is small
        
        // High DPI only matters with small screen
        if (highDPI && screenSizeMobile) mobileScore += 1;
        
        // ENTERPRISE: Higher threshold to prevent desktop false positives
        // Requires either user agent OR (small screen + additional indicators)
        return mobileScore >= 6;
    }
    
    /**
     * Get detailed device information for analytics
     * @returns {Object} Comprehensive device info
     */
    static getDeviceInfo() {
        const cacheKey = 'deviceInfo';
        
        if (MobileDetectionService._cache.has(cacheKey)) {
            return MobileDetectionService._cache.get(cacheKey);
        }
        
        const deviceInfo = {
            isMobile: this.isMobile(),
            isTablet: this.isTablet(),
            isDesktop: this.isDesktop(),
            deviceType: this.getDeviceType(),
            operatingSystem: this.getOperatingSystem(),
            browser: this.getBrowser(),
            screenInfo: this.getScreenInfo(),
            capabilities: this.getDeviceCapabilities(),
            timestamp: Date.now()
        };
        
        MobileDetectionService._cache.set(cacheKey, deviceInfo);
        return deviceInfo;
    }
    
    /**
     * Tablet detection
     * @returns {boolean}
     */
    static isTablet() {
        const userAgent = navigator.userAgent;
        const isTabletUA = /iPad|Android(?!.*Mobile)|Tablet|PlayBook|Silk/i.test(userAgent);
        const isLargeScreen = window.innerWidth >= 768 && window.innerWidth <= 1024;
        const hasTouchCapability = 'ontouchstart' in window;
        
        return isTabletUA || (isLargeScreen && hasTouchCapability);
    }
    
    /**
     * Desktop detection
     * @returns {boolean}
     */
    static isDesktop() {
        return !this.isMobile() && !this.isTablet();
    }
    
    /**
     * Get device type classification
     * @returns {string} 'mobile' | 'tablet' | 'desktop'
     */
    static getDeviceType() {
        if (this.isMobile()) return 'mobile';
        if (this.isTablet()) return 'tablet';
        return 'desktop';
    }
    
    /**
     * Operating system detection
     * @returns {string}
     */
    static getOperatingSystem() {
        const userAgent = navigator.userAgent;
        
        if (/Windows NT/i.test(userAgent)) return 'Windows';
        if (/Mac OS X/i.test(userAgent)) return 'macOS';
        if (/iPhone OS|iOS/i.test(userAgent)) return 'iOS';
        if (/Android/i.test(userAgent)) return 'Android';
        if (/Linux/i.test(userAgent)) return 'Linux';
        if (/CrOS/i.test(userAgent)) return 'Chrome OS';
        
        return 'Unknown';
    }
    
    /**
     * Browser detection
     * @returns {string}
     */
    static getBrowser() {
        const userAgent = navigator.userAgent;
        
        if (/Edg/i.test(userAgent)) return 'Microsoft Edge';
        if (/Chrome/i.test(userAgent) && !/Chromium/i.test(userAgent)) return 'Google Chrome';
        if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) return 'Safari';
        if (/Firefox/i.test(userAgent)) return 'Mozilla Firefox';
        if (/Opera|OPR/i.test(userAgent)) return 'Opera';
        
        return 'Unknown';
    }
    
    /**
     * Screen information
     * @returns {Object}
     */
    static getScreenInfo() {
        return {
            width: window.innerWidth,
            height: window.innerHeight,
            availWidth: screen.availWidth,
            availHeight: screen.availHeight,
            devicePixelRatio: window.devicePixelRatio,
            colorDepth: screen.colorDepth,
            pixelDepth: screen.pixelDepth,
            orientation: this.getOrientation()
        };
    }
    
    /**
     * Device capabilities detection
     * @returns {Object}
     */
    static getDeviceCapabilities() {
        return {
            touch: 'ontouchstart' in window,
            maxTouchPoints: navigator.maxTouchPoints || 0,
            geolocation: 'geolocation' in navigator,
            webgl: this._hasWebGL(),
            webgl2: this._hasWebGL2(),
            serviceWorker: 'serviceWorker' in navigator,
            pushNotifications: 'PushManager' in window,
            vibration: 'vibrate' in navigator,
            deviceMemory: navigator.deviceMemory || 'unknown',
            hardwareConcurrency: navigator.hardwareConcurrency || 'unknown',
            connection: this._getConnectionInfo()
        };
    }
    
    /**
     * Get device orientation
     * @returns {string}
     */
    static getOrientation() {
        if (screen.orientation) {
            return screen.orientation.type;
        }
        
        if (window.orientation !== undefined) {
            return Math.abs(window.orientation) === 90 ? 'landscape' : 'portrait';
        }
        
        return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
    }
    
    /**
     * Check if device is in landscape mode
     * @returns {boolean}
     */
    static isLandscape() {
        return this.getOrientation().includes('landscape');
    }
    
    /**
     * Check if device is in portrait mode
     * @returns {boolean}
     */
    static isPortrait() {
        return this.getOrientation().includes('portrait');
    }
    
    /**
     * WebGL detection
     * @private
     */
    static _hasWebGL() {
        try {
            const canvas = document.createElement('canvas');
            return !!(window.WebGLRenderingContext && 
                     (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
        } catch (e) {
            return false;
        }
    }
    
    /**
     * WebGL2 detection
     * @private
     */
    static _hasWebGL2() {
        try {
            const canvas = document.createElement('canvas');
            return !!(window.WebGL2RenderingContext && canvas.getContext('webgl2'));
        } catch (e) {
            return false;
        }
    }
    
    /**
     * Connection information
     * @private
     */
    static _getConnectionInfo() {
        if ('connection' in navigator) {
            const conn = navigator.connection;
            return {
                effectiveType: conn.effectiveType,
                downlink: conn.downlink,
                rtt: conn.rtt,
                saveData: conn.saveData
            };
        }
        return null;
    }
    
    /**
     * Subscribe to device changes
     * @param {Function} callback
     */
    static subscribe(callback) {
        MobileDetectionService._observers.add(callback);
        
        return () => {
            MobileDetectionService._observers.delete(callback);
        };
    }
    
    /**
     * Initialize detection system
     * @private
     */
    _initializeDetection() {
        // Clear cache on significant changes
        window.addEventListener('resize', this._handleResize.bind(this));
        window.addEventListener('orientationchange', this._handleOrientationChange.bind(this));
        
        // Clear cache on focus (in case user changed device settings)
        window.addEventListener('focus', this._handleFocus.bind(this));
    }
    
    /**
     * Setup resize observer for enterprise-grade responsiveness
     * @private
     */
    _setupResizeObserver() {
        if ('ResizeObserver' in window) {
            this._resizeObserver = new ResizeObserver(this._handleResize.bind(this));
            this._resizeObserver.observe(document.documentElement);
        }
    }
    
    /**
     * Handle resize events
     * @private
     */
    _handleResize() {
        this._clearCache();
        this._notifyObservers();
    }
    
    /**
     * Handle orientation change
     * @private
     */
    _handleOrientationChange() {
        // Wait for orientation change to complete
        setTimeout(() => {
            this._clearCache();
            this._notifyObservers();
        }, 100);
    }
    
    /**
     * Handle focus events
     * @private
     */
    _handleFocus() {
        // Partial cache clear - only clear volatile data
        MobileDetectionService._cache.delete('screenInfo');
        MobileDetectionService._cache.delete('deviceInfo');
    }
    
    /**
     * Clear detection cache
     * @private
     */
    _clearCache() {
        MobileDetectionService._cache.clear();
    }
    
    /**
     * Notify observers of changes
     * @private
     */
    _notifyObservers() {
        const deviceInfo = MobileDetectionService.getDeviceInfo();
        MobileDetectionService._observers.forEach(callback => {
            try {
                callback(deviceInfo);
            } catch (error) {
                console.error('Error in mobile detection observer:', error);
            }
        });
    }
    
    /**
     * Cleanup method for proper memory management
     */
    static cleanup() {
        if (this._instance?._resizeObserver) {
            this._instance._resizeObserver.disconnect();
        }
        
        MobileDetectionService._cache.clear();
        MobileDetectionService._observers.clear();
        this._instance = null;
    }
    
    /**
     * Get performance-optimized mobile check (cached)
     * @returns {boolean}
     */
    static isMobileCached() {
        return this.isMobile();
    }
    
    /**
     * Force refresh of mobile detection (bypass cache)
     * @returns {boolean}
     */
    static isMobileForceRefresh() {
        MobileDetectionService._cache.delete('isMobile');
        return this.isMobile();
    }
}

// Export singleton instance for convenience
export const mobileDetection = MobileDetectionService.getInstance();

// Export default for backward compatibility
export default MobileDetectionService;
